export type LanguageCode = 'fr' | 'en' | 'es';

// Returns the localized home path for the given language
export const getHomePath = (language: LanguageCode): string => {
  switch (language) {
    case 'en':
      return '/en';
    case 'es':
      return '/es';
    default:
      return '/';
  }
};

// Returns the localized profile path for the given language
export const getProfilePath = (language: LanguageCode): string => {
  switch (language) {
    case 'en':
      return '/en/profile';
    case 'es':
      return '/es/profile';
    default:
      return '/profile';
  }
};

// Returns the localized API docs path for the given language
export const getAPIDocsPath = (language: LanguageCode): string => {
  switch (language) {
    case 'en':
      return '/en/api-docs';
    case 'es':
      return '/es/api-docs';
    default:
      return '/api-docs';
  }
};

// Returns the localized favorites path for the given language
export const getFavoritesPath = (language: LanguageCode): string => {
  switch (language) {
    case 'en':
      return '/en/favorites';
    case 'es':
      return '/es/favorites';
    default:
      return '/favorites';
  }
};

// Returns the localized game category path for the given language and game name
export const getGameCategoryPath = (language: LanguageCode, gameName: string): string => {
  // Normaliser le slug en supprimant tous les caractères non alphanumériques
  const gameSlug = gameName
    .toLowerCase()
    .trim()
    .replace(/'/g, '') // Supprimer les apostrophes
    .replace(/[()]/g, '') // Supprimer les parenthèses
    .replace(/[:]/g, '') // Supprimer les deux-points
    .replace(/\s+/g, '-') // Espaces vers tirets
    .replace(/[^\w-]/g, '') // Garder seulement a-z, 0-9, - et _
    .replace(/--+/g, '-') // Supprimer les tirets multiples
    .replace(/^-+|-+$/g, ''); // Supprimer les tirets en début/fin
  
  switch (language) {
    case 'en':
      return `/en/game/${gameSlug}`;
    case 'es':
      return `/es/game/${gameSlug}`;
    default:
      return `/game/${gameSlug}`;
  }
};

// Returns the localized leaderboard path for the given language
export const getLeaderboardPath = (language: LanguageCode): string => {
  switch (language) {
    case 'en':
      return '/en/leaderboard';
    case 'es':
      return '/es/leaderboard';
    default:
      return '/leaderboard';
  }
};

// Returns the localized my votes path for the given language
export const getMyVotesPath = (language: LanguageCode): string => {
  switch (language) {
    case 'en':
      return '/en/my-votes';
    case 'es':
      return '/es/my-votes';
    default:
      return '/my-votes';
  }
};

// Returns the localized creator tools path for the given language
export const getCreatorToolsPath = (language: LanguageCode): string => {
  switch (language) {
    case 'en':
      return '/en/creator-tools';
    case 'es':
      return '/es/creator-tools';
    default:
      return '/creator-tools';
  }
};

// Returns the localized Votifier guide path for the given language
export const getVotifierGuidePath = (language: LanguageCode): string => {
  switch (language) {
    case 'en':
      return '/en/votifier-guide';
    case 'es':
      return '/es/guia-votifier';
    default:
      return '/guide-votifier';
  }
};

// Returns the localized add server path for the given language
export const getAddServerPath = (language: LanguageCode): string => {
  switch (language) {
    case 'en':
      return '/en/add-server';
    case 'es':
      return '/es/add-server';
    default:
      return '/add-server';
  }
};

// Returns the localized server detail path for the given language and identifier
export const getServerDetailPath = (
  language: LanguageCode,
  idOrSlug: { slug?: string; id?: string }
): string => {
  // Prefer slug for SEO-friendly URLs
  if (idOrSlug.slug) {
    // Normaliser le slug serveur
    const normalizedSlug = idOrSlug.slug
      .toLowerCase()
      .trim()
      .replace(/'/g, '') // Supprimer les apostrophes
      .replace(/[()]/g, '') // Supprimer les parenthèses
      .replace(/[:]/g, '') // Supprimer les deux-points
      .replace(/\s+/g, '-') // Espaces vers tirets
      .replace(/[^\w-]/g, '') // Garder seulement a-z, 0-9, - et _
      .replace(/--+/g, '-') // Supprimer les tirets multiples
      .replace(/^-+|-+$/g, ''); // Supprimer les tirets en début/fin
    
    switch (language) {
      case 'en':
        return `/en/server/${normalizedSlug}`;
      case 'es':
        return `/es/servidor/${normalizedSlug}`;
      default:
        return `/serveur/${normalizedSlug}`; // FR default without /fr prefix
    }
  }
  // Fallback to ID route (non-localized route exists only without prefix)
  if (idOrSlug.id) {
    return `/server/${idOrSlug.id}`;
  }
  return '/';
};

// Returns the localized article (spotlight) path. French has no prefix.
export const getSpotlightPath = (
  language: LanguageCode,
  date: string,
  slug: string
): string => {
  const cleanSlug = String(slug || '')
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (!cleanSlug) return getHomePath(language);
  switch (language) {
    case 'en':
      return `/en/spotlight/${date}/${cleanSlug}`;
    case 'es':
      return `/es/spotlight/${date}/${cleanSlug}`;
    default:
      return `/spotlight/${date}/${cleanSlug}`; // FR default without /fr prefix
  }
};
